import { useApolloClient, useQuery } from "@apollo/client/react";
import {
  forwardRef,
  startTransition,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  BoxIcon,
  MaximizeIcon,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import { IObject } from "@/__generated__/graphql";
import { detailViewForTypename } from "@/app/app/_/layouts/nodePanelConfig";
import { useOpenRecord } from "@/app/app/_/routing";
import {
  FieldDefinitionFieldType,
  FieldDefinitionReferencesType,
  FieldDefinitionReferenceType,
  FieldEventsType,
  FieldImperativeHandle,
  FieldSizeType,
  IObjectWithTypename,
  ReferenceValueOptionsType,
} from "@/components/@types";
import { BoxForOrganization } from "@/components/atoms/BoxForOrganization";
import { BoxWithAdornment } from "@/components/atoms/BoxWithAdornment";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { NodeSubmitButton } from "@/components/organisms/Node/NodeSubmitButton";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { Queries } from "@/constants";
import { EnumColors, Values } from "@/constants/values";
import { useEnumColor } from "@/hooks/useEnumColor";
import { useFocusedWithin } from "@/hooks/useFocusedWithin";
import { useHoverPrefetch } from "@/hooks/useHoverPrefetch";
import { iconsByTypename } from "@/objects/@icons";
import { idToIdentifier, idToTypename } from "@/objects/@utils";
import { findNearestContextNode } from "@/hooks/useContextTree";
import { State } from "@/state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";
import { Button } from "@/components/ui/button";
import { Typography, typographyStyles } from "@/components/ui/typography";

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_CONTAINER_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  // overflow: "hidden",
  textOverflow: "ellipsis",
  flexWrap: "nowrap",
};

const POPPER_FLOATING_STYLE = {
  padding: 0,
  width: "400px",
};

/**
 * What the reference popper is showing: the referenced node ("view"), the
 * picker ("select"), or a create form for a record that does not exist yet
 * ("create" — see ``mutationOptions.createTypename``).
 */
type FieldReferenceMode = "view" | "select" | "create" | null;

// ============================================================================
// Types
// ============================================================================

/**
 * Structure for tracking focusable refs within a field.
 * Uses a Set for O(1) add/remove and sorts by DOM position when navigating.
 */
type RefsStructure = {
  refsSet: Set<React.RefObject<HTMLElement>>;
  focusedIndex: number;
};

// ============================================================================
// Helper Functions
// ============================================================================

function normalizeValueOptions(
  valueOptions: ReferenceValueOptionsType<unknown> | undefined,
): {
  style: "enum" | "self" | "related" | "text";
  isNullable: boolean;
} {
  return {
    style: valueOptions?.style ?? "related",
    isNullable: valueOptions?.isNullable ?? false,
  };
}

/** A chip's words at its size: the theme's body level, spelled out. */
const REFERENCE_TEXT = {
  sm: "font-[family-name:Inter-Medium] text-xs leading-snug text-foreground",
  md: "font-[family-name:Inter-Medium] text-sm leading-snug text-foreground",
} as const;

function getScaleFactor(size?: "sm" | "md"): number {
  return size === "sm" ? 0.92 : 1;
}

function handleNodeSelection(
  data: IObject,
  events: FieldEventsType,
  onClose: () => void,
) {
  events?.onChangeField?.(data);
  events?.onSubmitField?.();
  onClose();
}

// ============================================================================
// Hook: useRefSetup
// ============================================================================

/**
 * Gets all valid refs sorted by DOM position.
 * This ensures Tab navigation follows visual/DOM order regardless of registration order.
 */
function getValidRefsSortedByDOMPosition(
  refsSet: Set<React.RefObject<HTMLElement>>,
): React.RefObject<HTMLElement>[] {
  // Filter to only refs with non-null .current
  const validRefs = Array.from(refsSet).filter((ref) => ref.current !== null);

  // Sort by DOM position using compareDocumentPosition
  validRefs.sort((a, b) => {
    if (!a.current || !b.current) return 0;
    const position = a.current.compareDocumentPosition(b.current);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });

  return validRefs;
}

function useRefSetup(refs: React.MutableRefObject<RefsStructure> | undefined): {
  registerRef: (el: HTMLElement | null) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  ref: React.RefObject<HTMLElement>;
} {
  const elementRef = useRef<HTMLElement>(null);

  // Register/unregister this ref when element mounts/unmounts
  const registerRef = useCallback(
    (el: HTMLElement | null) => {
      (elementRef as React.MutableRefObject<HTMLElement | null>).current = el;

      // If refs is not provided or not initialized, just update the fallback ref and return
      if (!refs?.current?.refsSet) return;

      if (el) {
        // Add to Set (automatically handles duplicates)
        refs.current.refsSet.add(elementRef);
      } else {
        // Element unmounting - remove from Set
        refs.current.refsSet.delete(elementRef);
      }
    },
    [refs, elementRef],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // If refs is not provided or not initialized, do nothing
      if (!refs?.current?.refsSet) return;

      if (e.key === "Tab") {
        // Get valid refs sorted by DOM position
        const sortedRefs = getValidRefsSortedByDOMPosition(
          refs.current.refsSet,
        );
        if (sortedRefs.length === 0) return;

        // Find our current position in the sorted refs
        const currentIndex = sortedRefs.findIndex((ref) => ref === elementRef);
        if (currentIndex === -1) return;

        e.preventDefault();
        e.stopPropagation();

        const nextIndex = e.shiftKey
          ? (currentIndex - 1 + sortedRefs.length) % sortedRefs.length
          : (currentIndex + 1) % sortedRefs.length;

        // Update focusedIndex and focus the element
        refs.current.focusedIndex = nextIndex;
        const nextRef = sortedRefs[nextIndex];
        if (nextRef?.current) {
          requestAnimationFrame(() => {
            nextRef.current?.focus();
          });
        }
      }
    },
    [refs, elementRef],
  );

  return { registerRef, onKeyDown, ref: elementRef };
}

// ============================================================================
// Add Button Component
// ============================================================================

function AddButton({
  onClick,
  disabled,
  refs,
  focusRef,
  size,
  label,
  tabIndex,
}: {
  onClick: (e: React.MouseEvent) => void;
  disabled: boolean;
  refs?: React.MutableRefObject<RefsStructure>;
  focusRef?: React.Ref<HTMLButtonElement | null>;
  size?: "sm" | "md";
  label?: string;
  tabIndex?: number;
}) {
  // Always call hooks unconditionally to follow React's rules of hooks
  const { registerRef: addButtonRef, onKeyDown: addButtonOnKeyDown } =
    useRefSetup(refs);

  const setButtonRef = useCallback(
    (element: HTMLButtonElement | null) => {
      addButtonRef(element);
      if (typeof focusRef === "function") {
        focusRef(element);
      } else if (focusRef) {
        focusRef.current = element;
      }
    },
    [addButtonRef, focusRef],
  );

  if (label != null) {
    return (
      <Typography
        component="button"
        type="button"
        ref={setButtonRef}
        disabled={disabled}
        tabIndex={tabIndex}
        data-testid="field-reference-add-button"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.stopPropagation();
            e.preventDefault();
            onClick(e as unknown as React.MouseEvent);
            return;
          }
          // Don't trap Tab for the inline-empty ``…`` control — parent
          // lists (e.g. triage rows) own Tab / arrow focus movement.
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClick(e);
        }}
        onMouseDown={(e) => {
          // Prevent focus steal + parent list row selection.
          e.preventDefault();
          e.stopPropagation();
        }}
        className={cn(
          "text-muted-foreground/80 [border:none] [background:none] p-0 [font:inherit]",
          disabled ? "cursor-default" : "cursor-pointer",
          "pointer-events-auto align-baseline [outline:none]",
          disabled
            ? ""
            : "hover:text-muted-foreground hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[currentColor] hover:underline-offset-2 hover:outline-[initial] [&:hover]:[outline-style:none] hover:[outline-width:initial] focus:text-muted-foreground focus:underline focus:[text-decoration-thickness:initial] focus:[text-decoration-style:initial] focus:decoration-[currentColor] focus:underline-offset-2 focus:outline-[initial] [&:focus]:[outline-style:none] focus:[outline-width:initial] focus-visible:text-muted-foreground focus-visible:underline focus-visible:[text-decoration-thickness:initial] focus-visible:[text-decoration-style:initial] focus-visible:decoration-[currentColor] focus-visible:underline-offset-2 focus-visible:outline-[initial] [&:focus-visible]:[outline-style:none] focus-visible:[outline-width:initial]",
        )}
      >
        {label}
      </Typography>
    );
  }

  const isCompact = size === "sm";

  return (
    <Button
      variant="outline"
      ref={setButtonRef}
      disabled={disabled}
      tabIndex={tabIndex}
      size="icon-xs"
      data-testid="field-reference-add-button"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.stopPropagation();
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent);
        } else {
          addButtonOnKeyDown(e);
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick(e);
      }}
      onMouseDown={(e) => {
        e.stopPropagation(); // Prevent Box from handling mouse events
      }}
      // Ensure pointer events work
      // Ensure button is in stacking context
      // Ensure button is above other elements
      className={cn(
        // In a small field's line it hangs over it, rather than moving it.
        isCompact ? "-my-0.5" : "m-0.5",
        "pointer-events-auto relative z-1",
      )}
    >
      <Icon
        ReactIcon={PlusIcon}
        size={isCompact ? "xs" : "sm"}
        style={
          isCompact
            ? {
                transform: "scale(0.8)",
                transformOrigin: "center",
              }
            : undefined
        }
      />
    </Button>
  );
}

/**
 * Create form for a record the picker could not find, shown in the picker's own
 * popper beside the field rather than in the full-screen create dialog.
 *
 * It is the same node HOC the rest of the app uses — title bar, groups, pending
 * operations — in ``create`` mode, seeded with whatever the user typed into the
 * picker as the new record's title. On submit the created node goes straight
 * back to the field that opened it.
 */
function FieldReferenceCreateCard({
  typename,
  title,
  entityIdentifier,
  size,
  onCancel,
  onCreated,
}: {
  typename: string;
  title: string;
  /**
   * Entity the new record belongs to — the one the picker lists options from,
   * i.e. the parent record's. Without it the create lands in whichever entity
   * the app is scoped to, so the record is invisible to the very picker that
   * created it.
   */
  entityIdentifier?: string;
  size: "sm" | "md";
  onCancel: () => void;
  onCreated: (node: IObject) => void;
}) {
  const { store } = useNodeSetup({ mode: "create", typename });
  const { events, metadata } = useNode(store);
  const titleFieldKey = store(
    useShallow((state) => (state.objectType?.titleFieldKey as string) || ""),
  );
  const isSetup = store(
    useShallow(
      (state) => state.objectType != null && state.fieldDefinitions.length > 0,
    ),
  );

  // Seeded after setup rather than through ``useNodeSetup``: its create branch
  // resets ``fieldValues`` to the bare typename once the object definition
  // lands, which would wipe a value passed in up front.
  useEffect(() => {
    if (!isSetup) return;
    store.setState({
      entityIdentifier: entityIdentifier ?? null,
      fieldValues: {
        ...store.getState().fieldValues,
        ...(titleFieldKey ? { [titleFieldKey]: title } : {}),
        // Readable by the object's own field definitions — a nested picker
        // inside the form (a tax rate's account, say) has to offer the entity
        // the record is being created in, not whichever one the app is scoped
        // to. Root keys starting with "_" are stripped before submit.
        _entityIdentifier: entityIdentifier ?? null,
      },
    });
  }, [isSetup, titleFieldKey, title, entityIdentifier, store]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <NodeTitleLayout
        store={store}
        mode="create"
        title={metadata.title}
        size={size}
        className="pl-3 pr-3 pt-3 bg-transparent shrink-0"
      />
      <NodeLayout
        store={store}
        size={size}
        isSingleClick={true}
        autofocus={true}
        forceLayoutMode="tabs"
      />
      <div className="flex justify-end gap-2 p-3 shrink-0 [border-top-width:1px] [border-top-style:solid] border-border">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <NodeSubmitButton
          store={store}
          size="sm"
          onSubmit={async () => {
            const result = (await events.onSubmitNode()) as
              | { data?: { createNode?: { data?: IObject[] } } }
              | undefined;
            const created = result?.data?.createNode?.data?.[0];
            if (created) onCreated(created);
          }}
        >
          Create
        </NodeSubmitButton>
      </div>
    </div>
  );
}

// ============================================================================
/**
 * The record a reference points at, as its popover shows it: read and set up
 * only while it is shown. Held by the popover itself, every reference in a
 * table's every row kept a record's store and its query, opened or not.
 */
function FieldReferenceViewCard({
  nodeId,
  title,
  identifier,
  size,
  onHasTableChange,
  onOpen,
}: {
  nodeId: string;
  title: string;
  identifier?: string;
  size?: "sm" | "md";
  onHasTableChange: (hasTable: boolean) => void;
  onOpen: () => void;
}) {
  const { loading, data } = useQuery(Queries.NODE_QUERY, {
    variables: {
      id: nodeId,
      startDate: State.getState().timeSeriesFilter.startDate?.toISOString(),
      endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
    },
  });
  const { store } = useNodeSetup({
    mode: "update",
    node: data?.node as IObjectWithTypename,
    typename: idToTypename(nodeId),
  });
  const { actions, fieldDefinitions } = useNode(store);
  // A record whose layout draws a table - a table of its own, or a tab of
  // its own component (an account's statements and transactions) - opens
  // as wide as a create card does: at the picker's width the table lost its
  // right-hand columns off the edge. The rest read at the picker's width.
  const hasTable = store(
    (state) =>
      state.fieldDefinitions.some((field) => field.type === "table") ||
      Boolean(state.objectType?.groups?.some((group) => group.component)),
  );
  useLayoutEffect(() => {
    onHasTableChange(hasTable);
  }, [hasTable, onHasTableChange]);

  return (
    <div
      // Claim the popper's available height (capped by the size
      // middleware in ``Popper.tsx``) instead of hard-coding a
      // ``maxHeight``. The previous fixed cap let the popper
      // shrink to e.g. 200px near the viewport bottom while this
      // Box still wanted ~420px, which cropped the inner
      // ``NodeLayout`` actions / footer behind the popper edge.
      className="flex flex-col flex-1 min-h-0 overflow-hidden"
    >
      <NodeTitleLayout
        store={store}
        title={title}
        isLoading={!data?.node || loading}
        size={size}
        className="pl-3 pr-3 pt-3 bg-transparent shrink-0"
        actions={actions}
        trailingDecorator={
          <FieldReferenceOpenButton
            record={{ id: nodeId, identifier }}
            onOpen={onOpen}
          />
        }
      />
      <NodeLayout
        autofocus={true}
        store={store}
        fieldDefinitions={fieldDefinitions}
        isLoading={!data?.node || loading}
        size={size}
        isInPopper
      />
    </div>
  );
}

// ============================================================================
/**
 * The popover's way to the record it shows: its page, or the fullscreen dialog
 * for a kind of record that has no page. Its own component so the router it
 * needs is asked for only where the button is drawn.
 */
function FieldReferenceOpenButton({
  record,
  onOpen,
}: {
  record: { id: string; identifier?: string };
  onOpen: () => void;
}) {
  const openRecord = useOpenRecord();
  const label = detailViewForTypename(idToTypename(record.id))
    ? "Open page"
    : "Open in dialog";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={label}
          onClick={() => {
            onOpen();
            openRecord(record);
          }}
        >
          <Icon ReactIcon={MaximizeIcon} size="sm" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

// Consolidated Popper Component
// ============================================================================

function FieldReferencePopper({
  nodeId,
  children,
  value,
  valueOptions,
  events,
  type,
  style,
  size,
  popperSize,
  includeAddButton = true,
  forceHide,
  focusRestoreRef: focusRestoreRefProp,
  mode,
  setMode,
  refs,
  // When true, an anchor click goes straight to "select" (replace
  // value) instead of "view" (open the referenced node's panel
  // inside the popper). Use this for picker-style references like
  // enums (e.g. Transaction.category) where clicking the value is
  // expected to swap it for another option, not drill in.
  selectOnAnchorClick = false,
}: {
  nodeId?: string;
  children: React.ReactNode;
  value?:
    | {
        __typename: string;
        name: string;
        id: string;
        identifier: string;
        color?: string;
      }
    | {
        __typename: string;
        name: string;
        id: string;
        identifier: string;
        color?: string;
      }[];
  valueOptions?: ReferenceValueOptionsType<unknown>;
  events?: FieldEventsType;
  type?: "reference" | "references";
  style?: React.CSSProperties;
  size?: "sm" | "md";
  popperSize?: "sm" | "md";
  includeAddButton?: boolean;
  forceHide?: boolean;
  focusRestoreRef?: React.RefObject<HTMLElement | null>;
  mode?: FieldReferenceMode;
  setMode?: (mode: FieldReferenceMode) => void;
  refs?: React.MutableRefObject<RefsStructure>;
  selectOnAnchorClick?: boolean;
}) {
  const [internalMode, setInternalMode] = useState<FieldReferenceMode>(null);
  // What the user typed into the picker before asking to create — it becomes
  // the new record's title in the create card.
  const [createTitle, setCreateTitle] = useState("");

  const actualMode = mode !== undefined ? mode : internalMode;
  const setActualMode = setMode !== undefined ? setMode : setInternalMode;

  const isTextStylePicker =
    valueOptions?.style === "text" && type === "reference";
  const isMultiselect = type === "references" || isTextStylePicker;
  const effectivePopperSize = popperSize ?? size;

  const activeMode = actualMode;
  const isOpen = actualMode !== null;

  const apolloClient = useApolloClient();
  const viewed = Array.isArray(value) ? value[0] : value;
  // Whether the record viewed draws a table, as its card says once it is open.
  const [hasTable, setHasTable] = useState(false);

  const handleSelection = (data: IObject) => {
    handleNodeSelection(data, events!, () => setActualMode(null));
  };

  const handleMultiSelection = useCallback(
    (checkedRows: IObject[]) => {
      if (isTextStylePicker) {
        events?.onChangeField?.(checkedRows[0] ?? null);
      } else {
        events?.onChangeField?.(checkedRows);
      }
      events?.onSubmitField?.();
      setActualMode(null);
    },
    [events, isTextStylePicker, setActualMode],
  );

  const getIsChecked = useCallback(
    (item: IObject): boolean => {
      if (Array.isArray(value)) {
        return value.some(
          (v) =>
            (v.id != null && v.id === item.id) ||
            (v.identifier != null && v.identifier === item.identifier),
        );
      }
      if (value?.id != null) {
        return value.id === item.id;
      }
      if (value?.identifier != null) {
        return value.identifier === item.identifier;
      }
      return false;
    },
    [value],
  );

  const popperIsOpen = isOpen;
  const setPopperIsOpen = useCallback(
    (open: boolean) => {
      if (open) {
        if (actualMode !== "select" && nodeId != null) {
          setActualMode("view");
        } else if (actualMode === null) {
          setActualMode("select");
        }
      } else {
        setActualMode(null);
      }
    },
    [actualMode, nodeId, setActualMode],
  );

  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const focusRestoreRef = focusRestoreRefProp ?? addButtonRef;

  const closePopperAndRestoreFocus = useCallback(() => {
    setPopperIsOpen(false);
    requestAnimationFrame(() => {
      focusRestoreRef.current?.focus();
    });
  }, [focusRestoreRef, setPopperIsOpen]);

  useEffect(() => {
    if (!popperIsOpen) {
      return;
    }
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closePopperAndRestoreFocus();
    };
    anchor.addEventListener("keydown", onKeyDown, true);
    return () => anchor.removeEventListener("keydown", onKeyDown, true);
  }, [closePopperAndRestoreFocus, popperIsOpen]);

  useEffect(() => {
    if (forceHide) setActualMode(null);
  }, [forceHide, setActualMode]);

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.stopPropagation();
    if (selectOnAnchorClick) {
      // Picker-style references (e.g. enum-rendered category): a
      // click on the displayed value is the "I want to change this"
      // affordance, not a drill-in. Skip the view step entirely.
      setActualMode("select");
      return;
    }
    if (nodeId != null) {
      setActualMode("view");
    } else {
      setActualMode("select");
    }
  };

  // NOTE: ``style`` is intentionally NOT spread onto this wrapper. The
  // wrapper is an internal flex container that holds the chip plus an
  // optional add button — callers that pass ``style`` mean to style the
  // chip itself (border/background/etc.), and double-applying it here
  // produced a visible nested outline. ``style`` is forwarded down to
  // ``ReferenceRelatedDisplay`` (and friends) via ``ReferenceDisplaySwitch``,
  // which is the correct layer to receive chip-level overrides.
  const anchor = (
    <div
      ref={anchorRef}
      onClick={handleClick}
      className={cn(
        // A block, as wide as what it holds: inline, it sat on a line of the
        // page's own taller text, and was taller to pick than to read.
        "flex",
        valueOptions?.inlineEmptyLabel != null
          ? "items-baseline"
          : "items-center",
        // Cut off across, at its width - never above or below, where a small
        // field's add button hangs over its line. As short as anything in a
        // row, as a box that hides what overflows it is.
        "justify-start gap-1 w-fit max-w-full min-w-0 overflow-x-clip pointer-events-auto",
      )}
    >
      {children}
      {includeAddButton && (
        <AddButton
          disabled={events?.onChangeField == null}
          focusRef={addButtonRef}
          size={size}
          tabIndex={valueOptions?.inlineEmptyTabIndex}
          label={
            events?.onChangeField != null &&
            valueOptions?.inlineEmptyLabel != null &&
            !children
              ? valueOptions.inlineEmptyLabel
              : undefined
          }
          onClick={() => {
            setActualMode("select");
          }}
          refs={refs}
        />
      )}
    </div>
  );

  return (
    <Popper
      forceHide={forceHide}
      anchor={anchor}
      isOpenState={[popperIsOpen, setPopperIsOpen]}
      focusRestoreRef={focusRestoreRef}
      restoreFocusOnEscape={focusRestoreRefProp != null ? "none" : "parent"}
      placement="bottom-start"
      mainAxisOffset={activeMode === "view" ? 8 : 4}
      crossAxisOffset={activeMode === "view" ? 4 : 0}
      style={{
        width: "fit-content",
        position: "relative",
        // Several, a block, as they are read alone: inline, they sat on a line
        // of the page's own taller text, and were taller to pick than to read.
        // One stays inline, where a cell's ``text-align`` places it.
        ...(type === "references" && { display: "block" }),
        // A related chip stops 8px short of a main-table cell's edge, as a
        // ``self`` reference does. The gap belongs here, where 100% is the
        // cell: on the chip it would resolve against this shrink-to-fit box
        // and cut 8px off a name that fits.
        ...(type === "reference" &&
          style?.width !== "100%" &&
          normalizeValueOptions(valueOptions).style === "related" && {
            maxWidth: "calc(100% - 8px)",
          }),
      }}
      floatingStyle={{
        // A record's layout, viewed or being made, is a card of its own and
        // rounds as one; the picker keeps a list's corners.
        borderRadius: activeMode === "select" ? "8px" : "12px",
        width: activeMode === "view" && hasTable ? "560px" : "400px",
        maxWidth: "calc(100vw - 32px)",
        ...(activeMode === "select"
          ? POPPER_FLOATING_STYLE
          : activeMode === "create"
            ? {
                ...POPPER_FLOATING_STYLE,
                // The create card carries a whole node layout, not a list — at
                // the picker's width a table inside it (a tax's rates, say)
                // loses its right-hand columns off the edge.
                width: "560px",
              }
            : {}),
      }}
      isClickable={false}
      isHoverable={false}
    >
      {activeMode === "view" && nodeId && (
        <FieldReferenceViewCard
          nodeId={nodeId}
          title={viewed?.name || ""}
          identifier={viewed?.identifier}
          size={effectivePopperSize}
          onHasTableChange={setHasTable}
          onOpen={() => setPopperIsOpen(false)}
        />
      )}
      {activeMode === "create" &&
        valueOptions?.mutationOptions?.createTypename && (
          <FieldReferenceCreateCard
            typename={valueOptions.mutationOptions.createTypename}
            title={createTitle}
            entityIdentifier={
              valueOptions.mutationOptions?.entityIdentifier ??
              (
                valueOptions.queryOptions?.variables as
                  | { entityIdentifiers?: string[] }
                  | undefined
              )?.entityIdentifiers?.[0]
            }
            size={effectivePopperSize ?? "md"}
            onCancel={() => setActualMode("select")}
            onCreated={(node) => {
              // The options are a plain list query, so Apollo has no way to
              // know the new record belongs in it — without this the picker
              // still cannot see what it just created.
              if (valueOptions.query) {
                void apolloClient.refetchQueries({
                  include: [valueOptions.query],
                });
              }
              handleSelection(node);
            }}
          />
        )}
      {activeMode === "select" && valueOptions && (
        <NodeList
          {...valueOptions}
          mutationOptions={
            // ``createTypename`` replaces the handler: the picker stays put and
            // swaps itself for a create form rather than handing off to the
            // full-screen dialog.
            valueOptions.mutationOptions?.createTypename
              ? {
                  ...valueOptions.mutationOptions,
                  onCreateNewOption: (filter: string) => {
                    setCreateTitle(filter);
                    setActualMode("create");
                  },
                }
              : valueOptions.mutationOptions
          }
          onSelectOption={handleSelection}
          isMultiselect={isMultiselect}
          maxCheckedRows={isTextStylePicker ? 1 : undefined}
          getIsChecked={getIsChecked}
          onSubmitCheckedRows={handleMultiSelection}
          size={effectivePopperSize}
          autofocus={true}
          // Hand the picker's submission callbacks + currently-selected
          // rows down to the inline "Create new …" handler in
          // ``mutationOptions.onCreateNewOption`` so it can append the
          // freshly-created row to the selection without re-implementing
          // the field's ``updateNode`` plumbing.
          events={events}
          currentValues={
            Array.isArray(value)
              ? (value as unknown as IObject[])
              : value?.id != null
                ? [value as unknown as IObject]
                : []
          }
        />
      )}
    </Popper>
  );
}

// ============================================================================
// Reference Display Style Components
// ============================================================================

function ReferenceEnumDisplay({
  value,
  valueOptions,
  size,
}: {
  value: {
    __typename: string;
    name: string;
    id: string;
    identifier: string;
    color?: string;
  };
  valueOptions: ReferenceValueOptionsType<unknown>;
  events?: FieldEventsType;
  index?: number;
  size?: "sm" | "md";
  style?: React.CSSProperties;
}) {
  return (
    <FieldEnum
      value={value?.name}
      valueOptions={{
        enum: [
          {
            key: "name",
            value: "name",
          },
        ],
        getColors(mode) {
          return {
            [value?.name]:
              EnumColors?.[value.color]?.[mode] || EnumColors.GREY_1?.[mode],
          };
        },
      }}
      isMultiline={false}
      size={size}
      variant={valueOptions?.variant}
      style={{ paddingLeft: 0, paddingRight: 0 }}
    />
  );
}

function ReferenceSelfDisplay({
  value,
  style,
  size,
  refs,
  events,
  isInList = false,
}: {
  value: {
    __typename: string;
    name: string;
    id: string;
    identifier: string;
    color?: string;
    entityId?: string;
    /** The logo of the company the record is of or from, drawn in place of the box. */
    logoUrl?: string | null;
  };
  valueOptions: ReferenceValueOptionsType<unknown>;
  events?: FieldEventsType;
  index?: number;
  size?: "sm" | "md";
  style?: React.CSSProperties;
  refs?: React.MutableRefObject<RefsStructure>;
  /**
   * One of several in a list: it keeps its own width, and the list is cut
   * off at the cell's edge. Alone, a reference gives way to a narrow column
   * with an ellipsis instead.
   */
  isInList?: boolean;
}) {
  const { name, identifier, id, entityId } = value;
  const displayText = name || identifier;
  const scaleFactor = getScaleFactor(size);
  const isInteractive = Boolean(events);
  const openRecord = useOpenRecord();

  // Check if panel is open and showing this node
  const activePanelNodeId = State((state) => state.activePanelNodeId);
  const isNodePanelOpen = State((state) => state.isNodePanelOpen);
  const isPanelShowingThisNode = activePanelNodeId === id && isNodePanelOpen;

  // Keyboard navigation setup
  const {
    ref: boxRef,
    registerRef: registerBoxRef,
    onKeyDown: boxOnKeyDown,
  } = useRefSetup(refs);
  const { registerRef: registerAdornmentRef, onKeyDown: adornmentOnKeyDown } =
    useRefSetup(refs);

  // Track focus within the component
  const isFocusedWithinBox = useFocusedWithin(boxRef);
  const fillWidth = style?.width === "100%";

  // Start the record's own query while the pointer is on its way to it. Both
  // destinations — the routed detail page and the node panel — run
  // NODE_QUERY_WITH_PENDING_CHANGE with exactly these variables and read
  // cache-first, so a preload that lands is already there when the click
  // arrives, and a click mid-flight deduplicates onto the pending request.
  const prefetch = useHoverPrefetch(() =>
    id
      ? {
          query: Queries.NODE_QUERY_WITH_PENDING_CHANGE,
          variables: {
            id,
            startDate:
              State.getState().timeSeriesFilter.startDate?.toISOString(),
            endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
          },
        }
      : null,
  );

  // Toggle node in side panel
  const handleAdornmentClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (id) {
      const state = State.getState();
      // If the Node panel is open and showing this node, close it and restore
      // focus to wherever the user was before they opened the panel.
      if (state.activePanelNodeId === id && state.isNodePanelOpen) {
        state.setIsNodePanelOpen(false);
        state.popFocusOrigin();
        return;
      }
      const el = document.activeElement as HTMLElement | null;
      const contextNode = el ? findNearestContextNode(el) : null;
      state.pushFocusOrigin({ contextNode, elementRef: el });
      // The FieldReferences adornment is an explicit "edit this object" entry
      // point — autofocus the first field so the user can start typing
      // immediately.
      state.openNodePanel(id, { autofocus: true });
    }
  };

  // Open the record: its detail page where the typename has one, otherwise
  // the fullscreen dialog (accounts, and anything else without a page).
  const handleMainClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!id) {
      return;
    }
    openRecord({ id, identifier, entityId }, e.currentTarget as HTMLElement);
  };

  return (
    <div
      ref={registerBoxRef}
      tabIndex={isInteractive ? 0 : -1}
      data-testid={`field-reference-self-${displayText}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          handleMainClick(e);
        } else {
          boxOnKeyDown(e);
        }
      }}
      onClick={(e) => {
        // Don't trigger main click if clicking on the adornment button
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        handleMainClick(e);
      }}
      {...prefetch}
      className={cn(
        // As wide as its name, up to the cell: a record's own chip is never
        // stretched across its column. A ``FieldTable`` cell passes
        // ``width: 100%`` so a related chip lines up with the column edges;
        // here it only lets the chip reach the padded cell's edge rather than
        // stop 8px short of it, as it does in a main table.
        "inline-flex items-center min-w-0 overflow-hidden relative [transition:all_0.2s] [min-height:unset] pt-0 pb-0 rounded-sm bg-accent [border:1px_solid_transparent] cursor-pointer [outline:0px_solid_transparent]!",
        isInList
          ? "max-w-none shrink-0"
          : fillWidth
            ? "max-w-full shrink"
            : "max-w-[calc(100%-8px)] shrink",
        size && REFERENCE_TEXT[size],
        "hover:bg-secondary",
        "focus-visible:border-muted-foreground/80 focus-visible:[outline:2px_solid_color-mix(in_srgb,var(--muted-foreground)_20%,transparent_80%)]!",
      )}
      // What the caller says goes over all that, but under the pointer and the
      // keys - no caller gives a self chip a ground of its own.
      style={{ ...style, ...(fillWidth && { width: "auto" }) }}
    >
      {/* Start adornment - always visible: the record's company's logo, which
          is the box, filling its square; or the box, where there is none or it
          cannot be drawn */}
      <BoxForOrganization
        data-testid="field-reference-self-icon"
        organization={{ logoUrl: value.logoUrl }}
        size={16 * scaleFactor ** 0.4}
        fallbackIcon={BoxIcon}
        isLogoFlush
        // Its size is its size whatever the page's box-sizing: a logo and the
        // box it stands in for take the same square.
        className="box-border rounded-(--reference-logo-radius) ml-(--reference-logo-inset) mr-1 p-0.5"
        style={
          {
            "--reference-logo-radius": `${6 * scaleFactor ** 0.4}px`,
            "--reference-logo-inset": `${0.5 * scaleFactor ** 3 * 8}px`,
          } as React.CSSProperties
        }
      />
      {/* Text with end adornment that slides in on focus/hover */}
      <BoxWithAdornment
        fontKey={`body-${size}`}
        delta={28}
        fillWidth={fillWidth}
        isOpenOverride={isFocusedWithinBox || undefined}
        endAdornment={
          <Button
            variant="outline"
            size="icon-xs"
            ref={registerAdornmentRef}
            tabIndex={isInteractive ? 0 : -1}
            data-testid={`field-reference-self-sidebar-${displayText}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleAdornmentClick(e);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                handleAdornmentClick(e);
              } else {
                adornmentOnKeyDown(e);
              }
            }}
          >
            <Icon
              ReactIcon={
                isPanelShowingThisNode
                  ? PanelRightCloseIcon
                  : PanelRightOpenIcon
              }
            />
          </Button>
        }
        style={{
          ...((typographyStyles as Record<string, React.CSSProperties>)[
            `body-${size}`
          ] as React.CSSProperties),
          position: "relative",
          height: `${26 * 1}px`,
          zIndex: 1,
          minWidth: 0,
          maxWidth: "100%",
          flex: fillWidth ? "1 1 auto" : "1 1 1",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            // Keep inline for main-table width measurement — block + minWidth:0
            // made BoxWithAdornment lock to a truncated scrollWidth.
            ...(fillWidth
              ? {
                  display: "block",
                  minWidth: 0,
                }
              : {}),
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: "underline",
            textDecorationColor:
              "color-mix(in srgb, var(--input) 50%, transparent)",
            textUnderlineOffset: "2px",
            paddingRight: "4px",
          }}
        >
          {name || identifier}
        </span>
      </BoxWithAdornment>
    </div>
  );
}

function ReferenceRelatedDisplay({
  value,
  events,
  size,
  style,
  onDelete,
  onFocus,
  onXButtonFocus,
  refs,
  isInList = false,
}: {
  value: {
    __typename: string;
    name: string;
    id: string;
    identifier: string;
    color?: string;
  };
  valueOptions: ReferenceValueOptionsType<unknown>;
  events?: FieldEventsType;
  index?: number;
  size?: "sm" | "md";
  style?: React.CSSProperties;
  onDelete: () => void;
  instanceId?: string;
  onFocus?: () => void;
  onXButtonFocus?: () => void;
  isOpenOverride?: boolean;
  editMode?: FieldReferenceMode;
  refs?: React.MutableRefObject<RefsStructure>;
  /**
   * One of several in a list: it keeps its own width, and the list is cut
   * off at the cell's edge. Alone, a reference gives way to a narrow column
   * with an ellipsis instead.
   */
  isInList?: boolean;
}) {
  const { name, identifier } = value;
  const displayText = name || identifier;
  const scaleFactor = getScaleFactor(size);

  // Always call hooks unconditionally to follow React's rules of hooks
  const {
    ref: boxRef,
    registerRef: registerBoxRef,
    onKeyDown: boxOnKeyDown,
  } = useRefSetup(refs);
  const { registerRef: registerXButtonRef, onKeyDown: xButtonOnKeyDown } =
    useRefSetup(refs);

  const isFocusedWithinBox = useFocusedWithin(boxRef);
  const fillWidth = style?.width === "100%";
  const canDelete = Boolean(events?.onDeleteField);

  return (
    <div
      ref={registerBoxRef}
      tabIndex={events ? 0 : -1}
      data-testid={`field-reference-related-${displayText}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) {
          if (!events) return;
          e.preventDefault();
          e.stopPropagation();
          onFocus?.();
        } else {
          boxOnKeyDown(e);
        }
      }}
      className={cn(
        // Sized like a ``self`` reference: its content is the preferred size,
        // not a floor, so in a column narrower than the name the chip gives
        // way instead of the anchor's ``overflow: hidden`` clipping the remove
        // button off its end. The main table's 8px gap is left by the popper
        // wrapper around it.
        fillWidth ? "flex w-full" : "inline-flex",
        isInList ? "max-w-none shrink-0" : "max-w-full shrink",
        "min-w-0 overflow-hidden h-(--reference-height) [transition:all_0.3s] rounded-sm bg-background [border:1px_solid_var(--border)] text-foreground flex-nowrap whitespace-nowrap cursor-pointer items-center [outline:0px_solid_transparent]! [box-shadow:inset_0_0_0_0px_transparent]",
        "hover:bg-accent",
        "focus-visible:border-muted-foreground/80 focus-visible:[outline:2px_solid_color-mix(in_srgb,var(--muted-foreground)_20%,transparent_80%)]!",
      )}
      // What the caller says goes last, but under the pointer and the keys -
      // no caller gives a related chip a ground or an edge of its own.
      style={
        {
          "--reference-height": `${26 * scaleFactor}px`,
          ...style,
        } as React.CSSProperties
      }
    >
      <div
        style={{
          border: `1px solid ${"var(--border)"}`,
          borderRadius: `${6 * scaleFactor ** 0.4}px`,
          minHeight: `${16 * scaleFactor ** 0.4}px`,
          minWidth: `${16 * scaleFactor ** 0.4}px`,
          maxHeight: `${16 * scaleFactor ** 0.4}px`,
          maxWidth: `${16 * scaleFactor ** 0.4}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--accent)",
          margin: "0px",
          marginLeft: `${0.5 * scaleFactor ** 3 * 8}px`,
          marginTop: "4px",
          marginBottom: "4px",
          marginRight: "4px",
          padding: "2px",
          position: "relative",
        }}
      >
        <Icon
          ReactIcon={iconsByTypename[value?.__typename] || BoxIcon}
          // ``currentColor`` so the icon inherits any chip-level color
          // override applied via ``style`` on the outer ReferenceRelatedDisplay
          // Box.
          style={{ color: "currentColor" }}
        />
      </div>
      <BoxWithAdornment
        fontKey={`body-${size}`}
        delta={28}
        fillWidth={fillWidth}
        isOpenOverride={events ? isFocusedWithinBox || undefined : false}
        endAdornment={
          canDelete ? (
            <Button
              variant="outline"
              size="icon-xs"
              ref={registerXButtonRef}
              tabIndex={0}
              data-testid={`field-reference-related-remove-${displayText}`}
              onClick={(e) => {
                if (!events?.onDeleteField) return;
                // Stop propagation to prevent the click from bubbling up to the parent Box
                e.stopPropagation();
                e.preventDefault();
                // Delete the reference (isNullable logic is in onDelete)
                onDelete?.();
              }}
              onKeyDown={(e) => {
                if (!events?.onDeleteField) return;
                // Pressing Enter when X button is focused deletes/replaces the reference
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete?.();
                } else {
                  xButtonOnKeyDown(e);
                }
              }}
            >
              <Icon ReactIcon={XIcon} />
            </Button>
          ) : undefined
        }
        style={{
          ...((typographyStyles as Record<string, React.CSSProperties>)[
            `body-${size}`
          ] as React.CSSProperties),
          position: "relative",
          height: `${26 * 1}px`,
          zIndex: 1,
          minWidth: 0,
          maxWidth: "100%",
          flex: fillWidth ? "1 1 auto" : undefined,
          overflow: "hidden",
        }}
      >
        <span
          style={{
            paddingRight: "4px",
            textAlign: style?.textAlign,
            // Keep inline outside fillWidth: block + minWidth:0 made
            // BoxWithAdornment lock to a truncated scrollWidth, leaving the
            // chip ellipsised after its parent grew back.
            ...(fillWidth
              ? {
                  display: "block",
                  minWidth: 0,
                }
              : {}),
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name || identifier}
        </span>
      </BoxWithAdornment>
    </div>
  );
}

function ReferenceTextDisplay({
  value,
  style,
  size,
  events,
}: {
  value: {
    __typename: string;
    name: string;
    id: string;
    identifier: string;
    color?: string;
  };
  valueOptions: ReferenceValueOptionsType<unknown>;
  events?: FieldEventsType;
  index?: number;
  size?: "sm" | "md";
  style?: React.CSSProperties;
}) {
  const { name, identifier } = value;
  const enumColor = useEnumColor(EnumColors.GREY_1);
  const isInlineLink = style?.backgroundColor === "transparent";
  const displayText = name || identifier;

  if (isInlineLink) {
    return (
      <span
        className={cn(
          "inline align-baseline bg-transparent p-0 [font:inherit] [font-size:inherit] [line-height:inherit] cursor-pointer [text-decoration:none] [outline:none]",
          style?.color != null
            ? "text-(--reference-link-color)"
            : "[color:inherit]",
          "hover:text-muted-foreground hover:[text-decoration:underline] hover:[text-decoration-color:currentColor] hover:[text-underline-offset:2px] hover:[outline:none]",
          "focus:text-muted-foreground focus:[text-decoration:underline] focus:[text-decoration-color:currentColor] focus:[text-underline-offset:2px] focus:[outline:none]",
          "focus-visible:text-muted-foreground focus-visible:[text-decoration:underline] focus-visible:[text-decoration-color:currentColor] focus-visible:[text-underline-offset:2px] focus-visible:[outline:none]",
        )}
        style={
          { "--reference-link-color": style?.color } as React.CSSProperties
        }
      >
        {displayText}
      </span>
    );
  }

  return (
    <span
      style={{
        display: "inline",
        verticalAlign: "baseline",
        cursor: events ? "pointer" : undefined,
        ...((typographyStyles as Record<string, React.CSSProperties>)[
          `body-${size ?? "sm"}`
        ] as React.CSSProperties),
        alignItems: "center",
        justifyContent: "flex-start",
        height: "1em",
        borderRadius: "0.25rem",
        color: enumColor.color,
        backgroundColor: enumColor.backgroundColor,
        paddingLeft: "4px",
        paddingRight: "4px",
        ...style,
      }}
    >
      {`@${displayText}`}
    </span>
  );
}

// ============================================================================
// Reference Display Switch Component
// ============================================================================

const ReferenceDisplaySwitch = forwardRef<
  HTMLElement,
  {
    value:
      | {
          __typename: string;
          name: string;
          id: string;
          identifier: string;
          color?: string;
        }
      | {
          __typename: string;
          name: string;
          id: string;
          identifier: string;
          color: string;
        };
    valueOptions: ReferenceValueOptionsType<unknown>;
    events?: FieldEventsType;
    index?: number;
    size?: "sm" | "md";
    style?: React.CSSProperties;
    onDelete?: () => void;
    instanceId?: string;
    onFocus?: () => void;
    onXButtonFocus?: () => void;
    isOpenOverride?: boolean;
    editMode?: FieldReferenceMode;
    refs?: React.MutableRefObject<RefsStructure>;
    /** One of several in a list - see ``ReferenceSelfDisplay``'s. */
    isInList?: boolean;
  }
>(function ReferenceDisplaySwitch(
  {
    value,
    valueOptions,
    events,
    index,
    size,
    style,
    onDelete,
    instanceId,
    onFocus,
    onXButtonFocus,
    isOpenOverride,
    editMode,
    refs,
    isInList,
  },
  ref,
) {
  const { id, identifier } = value;
  const { style: styleType } = normalizeValueOptions(valueOptions);

  // A record with no id or identifier is not made yet - an account from the
  // catalogue a suggestion makes when it is accepted: named, with nothing to
  // open. With no name either, there is nothing to show.
  if (id == null && !identifier) {
    const name = (value as { name?: string | null }).name;
    return name ? (
      <Typography
        level={`body-${size ?? "sm"}`}
        className="truncate text-muted-foreground pl-2 pr-2"
        style={style}
      >
        {name}
      </Typography>
    ) : null;
  }

  switch (styleType) {
    case "enum":
      return (
        <ReferenceEnumDisplay
          value={value}
          valueOptions={valueOptions}
          events={events}
          index={index}
          size={size}
          style={style}
        />
      );
    case "self":
      return (
        <ReferenceSelfDisplay
          value={value}
          valueOptions={valueOptions}
          events={events}
          index={index}
          size={size}
          style={style}
          refs={refs}
          isInList={isInList}
        />
      );
    case "related":
      return (
        <ReferenceRelatedDisplay
          value={value}
          valueOptions={valueOptions}
          events={events}
          index={index}
          size={size}
          style={style}
          onDelete={onDelete!}
          instanceId={instanceId}
          onFocus={onFocus}
          onXButtonFocus={onXButtonFocus}
          isOpenOverride={isOpenOverride}
          editMode={editMode}
          refs={refs}
          isInList={isInList}
        />
      );
    case "text":
      return (
        <ReferenceTextDisplay
          value={value}
          valueOptions={valueOptions}
          events={events}
          index={index}
          size={size}
          style={style}
        />
      );
    default:
      return null;
  }
});

// ============================================================================
// Reference Value Component
// ============================================================================

function FieldReferenceValue<T extends FieldDefinitionReferenceType<T>>({
  events,
  value,
  valueOptions,
  index,
  size,
  popperSize,
  style,
  editModeState,
  instanceId,
  isOpenOverride,
  refsRef,
  includeAddButton = true,
}: FieldDefinitionFieldType<FieldDefinitionReferenceType<T>> & {
  index?: number;
  editModeState: [FieldReferenceMode, (mode: FieldReferenceMode) => void];
  instanceId?: string;
  isOpenOverride?: boolean;
  popperSize?: "sm" | "md";
  refsRef?: React.MutableRefObject<RefsStructure>;
  includeAddButton?: boolean;
}) {
  // Defensive: value can become null mid-stream when Apollo evicts a
  // referenced cache entry (e.g. a workflow node whose pending change was
  // just confirmed/rejected). Re-render as nothing rather than crashing
  // the entire app via the ErrorBoundary.
  if (value == null) return null;
  const { id } = value;
  const { style: styleType, isNullable } = normalizeValueOptions(valueOptions);
  const [editMode, setEditMode] = editModeState;

  // Root refs structure - always call useRef to follow React's rules of hooks
  const internalRefsRef = useRef<RefsStructure>({
    refsSet: new Set(),
    focusedIndex: 0,
  });
  const refs = refsRef || internalRefsRef;

  const handleDeleteClick = () => {
    if (isNullable === false && typeof index !== "number") {
      // Not nullable and single reference: must replace with another value
      setEditMode("select");
    } else {
      // Nullable or array item: delete the reference
      events?.onDeleteField?.(index);
      events?.onSubmitField?.();
      // After React re-renders, focus the add button (first element in refs after deletion)
      requestAnimationFrame(() => {
        if (!refs?.current?.refsSet) return;
        const sortedRefs = getValidRefsSortedByDOMPosition(
          refs.current.refsSet,
        );
        // Find the add button (should be the remaining focusable element)
        const addButton = sortedRefs.find(
          (r) => r.current?.tagName === "BUTTON",
        );
        if (addButton?.current) {
          addButton.current.focus();
          refs.current.focusedIndex = sortedRefs.indexOf(addButton);
        }
      });
    }
  };

  const handleReferenceFocus = () => {
    // When reference is focused, open the NodeLayoutPopper (view mode)
    if (editMode !== "view") {
      // Use startTransition to avoid blocking the main thread
      startTransition(() => {
        setEditMode("view");
      });
    }
  };

  const handleXButtonFocus = () => {
    // When X button is focused, open the replace popper (select mode)
    if (editMode !== "select") {
      // Use startTransition to avoid blocking the main thread
      startTransition(() => {
        setEditMode("select");
      });
    }
  };

  const reference = (
    <ReferenceDisplaySwitch
      value={value}
      valueOptions={valueOptions}
      events={events}
      index={index}
      size={size}
      style={style}
      onDelete={handleDeleteClick}
      instanceId={instanceId}
      onFocus={handleReferenceFocus}
      onXButtonFocus={handleXButtonFocus}
      isOpenOverride={isOpenOverride}
      editMode={editMode}
      refs={refs}
    />
  );

  // Empty, unless it names a record not made yet (see ``ReferenceDisplaySwitch``).
  const isEmpty = id == null && !(value as { name?: string | null }).name;

  // Handle "related" style with replace popper
  if (styleType === "related") {
    return (
      <FieldReferencePopper
        nodeId={id}
        value={value}
        valueOptions={valueOptions}
        events={events!}
        type="reference"
        mode={editMode}
        setMode={setEditMode}
        includeAddButton={
          includeAddButton && isEmpty && events?.onChangeField != null
        }
        style={style}
        size={size}
        popperSize={popperSize}
        refs={refs}
      >
        {/* Pass null (not a null-rendering element) so ``inlineEmptyLabel``
            can detect an empty anchor via ``!children``. */}
        {isEmpty ? null : reference}
      </FieldReferencePopper>
    );
  }

  // Handle "self" style - no popper, click handlers are in
  // ReferenceSelfDisplay (drills into the referenced node's panel).
  if (styleType === "self") {
    return reference;
  }

  // Handle "enum"/"text" styles: previously these returned the bare
  // display with no editing path, which made fields like
  // ``Transaction.categoryObj`` (style: "enum") effectively read-only
  // even when ``updateProps.isEditable: true``. When the field is
  // wired up for editing (``events.onChangeField`` present), wrap in
  // a select-mode popper so a click on the value opens the picker.
  if (events?.onChangeField != null) {
    return (
      <FieldReferencePopper
        nodeId={id}
        value={value}
        valueOptions={valueOptions}
        events={events}
        type="reference"
        mode={editMode}
        setMode={setEditMode}
        includeAddButton={includeAddButton && isEmpty}
        style={style}
        size={size}
        popperSize={popperSize}
        refs={refs}
        selectOnAnchorClick={true}
      >
        {isEmpty ? null : reference}
      </FieldReferencePopper>
    );
  }

  return reference;
}

// ============================================================================
// References Value Component
// ============================================================================

function FieldReferencesValue<T extends FieldDefinitionReferencesType<T>>({
  events,
  value,
  valueOptions,
  size,
  popperSize,
  style,
  editModeState,
  refsRef,
  includeAddButton = true,
  focusRestoreRef,
}: FieldDefinitionFieldType<FieldDefinitionReferencesType<T>> & {
  editModeState: [FieldReferenceMode, (mode: FieldReferenceMode) => void];
  popperSize?: "sm" | "md";
  refsRef?: React.MutableRefObject<RefsStructure>;
  includeAddButton?: boolean;
  focusRestoreRef?: React.RefObject<HTMLElement | null>;
}) {
  const values = value || [];
  const hasValues = values.length > 0;
  const [editMode, setEditMode] = editModeState;
  const [viewNodeId, setViewNodeId] = useState<string | null>(null);

  // Root refs structure - always call useRef to follow React's rules of hooks
  const internalRefsRef = useRef<RefsStructure>({
    refsSet: new Set(),
    focusedIndex: 0,
  });
  const refs = refsRef || internalRefsRef;

  useEffect(() => {
    if (editMode === null) {
      setViewNodeId(null);
    }
  }, [editMode]);

  const focusAddButtonAfterDelete = useCallback(() => {
    requestAnimationFrame(() => {
      if (!refs?.current?.refsSet) return;
      const sortedRefs = getValidRefsSortedByDOMPosition(refs.current.refsSet);
      const addButton = sortedRefs.find((r) => r.current?.tagName === "BUTTON");
      if (addButton?.current) {
        addButton.current.focus();
        refs.current.focusedIndex = sortedRefs.indexOf(addButton);
      }
    });
  }, [refs]);

  const handleChipFocus = useCallback(
    (nodeId: string) => {
      if (editMode !== "view") {
        setViewNodeId(nodeId);
        startTransition(() => {
          setEditMode("view");
        });
      }
    },
    [editMode, setEditMode],
  );

  const handleOpenSelect = useCallback(() => {
    if (editMode !== "select") {
      setViewNodeId(null);
      startTransition(() => {
        setEditMode("select");
      });
    }
  }, [editMode, setEditMode]);

  const handleDelete = useCallback(
    (index: number) => {
      events?.onDeleteField?.(index);
      events?.onSubmitField?.();
      focusAddButtonAfterDelete();
    },
    [events, focusAddButtonAfterDelete],
  );

  // Read-only mode: render values without interaction
  if (events == null) {
    if (values.length === 0) {
      return null;
    }
    return (
      <div
        style={{
          ...DEFAULT_CONTAINER_STYLE,
          // Each keeps its width; the list is cut off at the cell's edge.
          overflow: "hidden",
          gap: "4px",
        }}
      >
        {values.map((value, index) => (
          <ReferenceDisplaySwitch
            key={index}
            value={value}
            valueOptions={valueOptions}
            events={events}
            index={index}
            size={size}
            isInList
          />
        ))}
      </div>
    );
  }

  // Editable mode: render with selection popper
  return (
    <FieldReferencePopper
      nodeId={editMode === "view" ? (viewNodeId ?? undefined) : undefined}
      value={values}
      valueOptions={valueOptions}
      events={events}
      type="references"
      mode={editMode}
      setMode={setEditMode}
      style={{
        cursor: "pointer",
        ...DEFAULT_CONTAINER_STYLE,
        // Each keeps its width; the list is cut off at the cell's edge.
        overflow: "hidden",
        gap: "4px",
      }}
      includeAddButton={includeAddButton}
      focusRestoreRef={focusRestoreRef}
      size={size}
      popperSize={popperSize}
      refs={refs}
    >
      {hasValues &&
        values.map((value, index) => (
          <ReferenceDisplaySwitch
            key={index}
            value={value}
            valueOptions={valueOptions}
            events={events}
            index={index}
            size={size}
            style={style}
            onDelete={() => handleDelete(index)}
            onFocus={() => handleChipFocus(value.id ?? value.identifier ?? "")}
            onXButtonFocus={handleOpenSelect}
            editMode={editMode}
            refs={refs}
            isInList
          />
        ))}
    </FieldReferencePopper>
  );
}

// ============================================================================
// Skeleton Components
// ============================================================================

function FieldReferenceSkeleton({ size }: { size: FieldSizeType }) {
  return (
    <Skeleton
      variant="text"
      level={`body-${size}`}
      style={{
        width: "100%",
        maxWidth: Values.DEFAULT_COLUMN_WIDTH,
      }}
    />
  );
}

function FieldReferencesSkeleton({ size }: { size: FieldSizeType }) {
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <Skeleton
        variant="text"
        level={`body-${size}`}
        style={{ width: "33%", maxWidth: Values.DEFAULT_COLUMN_WIDTH }}
      />
    </div>
  );
}

// ============================================================================
// Exported Components
// ============================================================================

export function FieldReference<T extends FieldDefinitionReferenceType<T>>({
  isLoading,
  style,
  size,
  popperSize,
  ref,
  isOpenOverride,
  includeAddButton = true,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionReferenceType<T>> & {
  index?: number;
  ref?: React.Ref<FieldImperativeHandle>;
  isOpenOverride?: boolean;
  popperSize?: "sm" | "md";
  includeAddButton?: boolean;
}) {
  const [editMode, setEditMode] = useState<FieldReferenceMode>(null);
  const instanceId = useId().replace(/:/g, ""); // Generate unique ID for this component instance

  const refsRef = useRef<RefsStructure>({
    refsSet: new Set(),
    focusedIndex: 0,
  });

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        // Safety check - ensure refs structure is initialized
        if (!refsRef.current?.refsSet) return;

        // Focus the element at focusedIndex, or first if none set
        const sortedRefs = getValidRefsSortedByDOMPosition(
          refsRef.current.refsSet,
        );
        if (sortedRefs.length === 0) return;
        const refToFocus = sortedRefs[0];
        if (refToFocus?.current) {
          refToFocus.current.focus();
          refsRef.current.focusedIndex = 0;
        }
      },
      unsetFocus: () => {
        setEditMode(null);
      },
      openSelect: () => {
        setEditMode("select");
      },
    }),
    [],
  );

  if (isLoading) {
    return <FieldReferenceSkeleton size={size} />;
  }

  return (
    <FieldReferenceValue
      {...props}
      size={size}
      popperSize={popperSize}
      style={style}
      editModeState={[editMode, setEditMode]}
      instanceId={instanceId}
      isOpenOverride={isOpenOverride}
      refsRef={refsRef}
      includeAddButton={includeAddButton}
    />
  );
}

export function FieldReferences<T extends FieldDefinitionReferencesType<T>>({
  isLoading,
  style,
  size,
  popperSize,
  ref,
  includeAddButton = true,
  focusRestoreRef,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionReferencesType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
  popperSize?: "sm" | "md";
  includeAddButton?: boolean;
  focusRestoreRef?: React.RefObject<HTMLElement | null>;
}) {
  const [editMode, setEditMode] = useState<FieldReferenceMode>(null);

  const refsRef = useRef<RefsStructure>({
    refsSet: new Set(),
    focusedIndex: 0,
  });

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        // Safety check - ensure refs structure is initialized
        if (!refsRef.current?.refsSet) return;

        // Focus the element at focusedIndex, or first if none set
        const sortedRefs = getValidRefsSortedByDOMPosition(
          refsRef.current.refsSet,
        );
        if (sortedRefs.length === 0) return;

        const refToFocus = sortedRefs[0];
        if (refToFocus?.current) {
          refToFocus.current.focus();
          refsRef.current.focusedIndex = 0;
        }
      },
      unsetFocus: () => {
        setEditMode(null);
      },
      openSelect: () => {
        setEditMode("select");
      },
    }),
    [],
  );

  if (isLoading) {
    return <FieldReferencesSkeleton size={size} />;
  }

  return (
    <FieldReferencesValue
      {...props}
      size={size}
      popperSize={popperSize}
      style={style}
      editModeState={[editMode, setEditMode]}
      refsRef={refsRef}
      includeAddButton={includeAddButton}
      focusRestoreRef={focusRestoreRef}
    />
  );
}
